import { Stack } from '@mantine/core';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { BLACKBAUD_OAUTH_CALLBACK_PATH, getBlackbaudRedirectUri } from '@feathr/blackbox';
import { Button } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import { useAccount, useLocalUrl, useStore } from '@feathr/extender/state';
import { cssVar } from '@feathr/hooks';

import { getOAuthAccountId, getOAuthReturnPath } from './BlackbaudOAuthCallback.utils';

function BlackbaudOAuthCallback(): JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const localUrl = useLocalUrl();
  const account = useAccount();
  const { BlackbaudRaisersEdgeIntegrations } = useStore();
  const [error, setError] = useState<string | null>(null);
  const returnPath = getOAuthReturnPath(
    new URLSearchParams(globalThis.location.search).get('state'),
    localUrl('/settings/integrations/raisers-edge'),
  );

  useEffect(() => {
    async function handleOAuthCallback(): Promise<void> {
      // Move to the account that started the flow before any account-scoped request.
      const returnAccountId = getOAuthAccountId(returnPath);
      if (returnAccountId && returnAccountId !== account.id) {
        history.replace(
          `/${returnAccountId}${BLACKBAUD_OAUTH_CALLBACK_PATH}${globalThis.location.search}`,
        );
        return;
      }

      const params = new URLSearchParams(globalThis.location.search);
      const code = params.get('code');
      const oauthError = params.get('error');

      if (oauthError === 'access_denied') {
        setError(
          t(
            "Feathr didn't get access to your Raiser's Edge NXT data. To connect, try again and allow access in Blackbaud.",
          ),
        );
        return;
      }

      if (oauthError) {
        setError(params.get('error_description') || oauthError);
        return;
      }

      if (!code) {
        setError(t("Blackbaud didn't send an authorization code. Try to connect again."));
        return;
      }

      try {
        const listResponse = BlackbaudRaisersEdgeIntegrations.list();
        await when(() => !listResponse.isPending);

        const integration = listResponse.models.length > 0 ? listResponse.models[0] : undefined;

        if (!integration) {
          setError(
            t("This account has no Raiser's Edge NXT integration. Go back and connect again."),
          );
          return;
        }

        const redirectUri = getBlackbaudRedirectUri(globalThis.location.origin);
        await integration.authenticate(code, redirectUri);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        setError(errorMessage);
        return;
      }

      history.push(returnPath);
    }

    void handleOAuthCallback();
  }, [BlackbaudRaisersEdgeIntegrations, account.id, history, returnPath, t]);

  const pageTitle = t("Blackbaud Raiser's Edge NXT");

  if (!error) {
    return (
      <Page loading={true} title={pageTitle}>
        {null}
      </Page>
    );
  }

  return (
    <Page title={pageTitle}>
      <Stack
        align={'center'}
        gap={cssVar('--spacing-4')}
        justify={'center'}
        mih={'60vh'}
        ta={'center'}
      >
        <div>
          <h4>{t("Couldn't connect to Raiser's Edge NXT")}</h4>
          <p>{error}</p>
        </div>
        <Button link={returnPath} type={'primary'}>
          {t("Back to Raiser's Edge NXT")}
        </Button>
      </Stack>
    </Page>
  );
}

export default observer(BlackbaudOAuthCallback);
